import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, History, Calculator, Zap, Copy, Check } from 'lucide-react';

interface HistoryEntry {
  id: number;
  from: string;
  to: string;
  timestamp: string;
}

interface UnitDefinition {
  factor: number;
  category: string;
  symbol: string;
  fullName: string;
  description: string;
}

type UnitName = 'meters_per_second' | 'kilometers_per_hour' | 'miles_per_hour' | 'feet_per_second' | 'knots' | 'mach' | 'speed_of_light' | 'kilometers_per_second' | 'miles_per_second' | 'centimeters_per_second' | 'millimeters_per_second' | 'inches_per_second' | 'yards_per_second' | 'furlongs_per_fortnight' | 'meters_per_minute' | 'kilometers_per_minute' | 'feet_per_minute' | 'miles_per_minute' | 'meters_per_hour' | 'feet_per_hour' | 'beaufort_scale' | 'speed_of_sound' | 'light_speed_vacuum' | 'earths_escape_velocity';

const SpeedConverter: React.FC = () => {
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [fromUnit, setFromUnit] = useState<UnitName>('kilometers_per_hour');
  const [toUnit, setToUnit] = useState<UnitName>('miles_per_hour');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [precision, setPrecision] = useState<number>(4);

  // Comprehensive speed unit definitions with conversion factors to meters per second
  const units: Record<UnitName, UnitDefinition> = {
    // Common Units
    'meters_per_second': { 
      factor: 1, 
      category: 'Common', 
      symbol: 'm/s', 
      fullName: 'Meters per Second',
      description: 'SI base unit for speed'
    },
    'kilometers_per_hour': { 
      factor: 0.277778, 
      category: 'Common', 
      symbol: 'km/h', 
      fullName: 'Kilometers per Hour',
      description: 'Standard automotive speed'
    },
    'miles_per_hour': { 
      factor: 0.44704, 
      category: 'Common', 
      symbol: 'mph', 
      fullName: 'Miles per Hour',
      description: 'US/UK automotive speed'
    },
    'feet_per_second': { 
      factor: 0.3048, 
      category: 'Common', 
      symbol: 'ft/s', 
      fullName: 'Feet per Second',
      description: 'Engineering and sports'
    },
    'knots': { 
      factor: 0.514444, 
      category: 'Common', 
      symbol: 'kn', 
      fullName: 'Knots',
      description: 'Nautical and aviation speed'
    },
    
    // Scientific/Astronomical
    'mach': { 
      factor: 343, 
      category: 'Scientific', 
      symbol: 'Ma', 
      fullName: 'Mach Number',
      description: 'Speed of sound ratio (at sea level)'
    },
    'speed_of_sound': { 
      factor: 343, 
      category: 'Scientific', 
      symbol: 'c_sound', 
      fullName: 'Speed of Sound',
      description: 'Sound speed in air (20°C)'
    },
    'speed_of_light': { 
      factor: 299792458, 
      category: 'Scientific', 
      symbol: 'c', 
      fullName: 'Speed of Light',
      description: 'Universal speed limit'
    },
    'light_speed_vacuum': { 
      factor: 299792458, 
      category: 'Scientific', 
      symbol: 'c₀', 
      fullName: 'Light Speed (Vacuum)',
      description: 'Exact speed of light in vacuum'
    },
    'earths_escape_velocity': { 
      factor: 11200, 
      category: 'Scientific', 
      symbol: 'v_esc', 
      fullName: 'Earth Escape Velocity',
      description: 'Minimum speed to escape Earth'
    },
    
    // High Speed Units
    'kilometers_per_second': { 
      factor: 1000, 
      category: 'High Speed', 
      symbol: 'km/s', 
      fullName: 'Kilometers per Second',
      description: 'Astronomical speeds'
    },
    'miles_per_second': { 
      factor: 1609.344, 
      category: 'High Speed', 
      symbol: 'mi/s', 
      fullName: 'Miles per Second',
      description: 'High velocity measurements'
    },
    
    // Small Units
    'centimeters_per_second': { 
      factor: 0.01, 
      category: 'Small Scale', 
      symbol: 'cm/s', 
      fullName: 'Centimeters per Second',
      description: 'Laboratory measurements'
    },
    'millimeters_per_second': { 
      factor: 0.001, 
      category: 'Small Scale', 
      symbol: 'mm/s', 
      fullName: 'Millimeters per Second',
      description: 'Precision measurements'
    },
    'inches_per_second': { 
      factor: 0.0254, 
      category: 'Small Scale', 
      symbol: 'in/s', 
      fullName: 'Inches per Second',
      description: 'Engineering applications'
    },
    'yards_per_second': { 
      factor: 0.9144, 
      category: 'Small Scale', 
      symbol: 'yd/s', 
      fullName: 'Yards per Second',
      description: 'Sports and athletics'
    },
    
    // Time-based Variants
    'meters_per_minute': { 
      factor: 0.0166667, 
      category: 'Time Variants', 
      symbol: 'm/min', 
      fullName: 'Meters per Minute',
      description: 'Walking and slow speeds'
    },
    'kilometers_per_minute': { 
      factor: 16.6667, 
      category: 'Time Variants', 
      symbol: 'km/min', 
      fullName: 'Kilometers per Minute',
      description: 'Fast transportation'
    },
    'feet_per_minute': { 
      factor: 0.00508, 
      category: 'Time Variants', 
      symbol: 'ft/min', 
      fullName: 'Feet per Minute',
      description: 'Industrial applications'
    },
    'miles_per_minute': { 
      factor: 26.8224, 
      category: 'Time Variants', 
      symbol: 'mi/min', 
      fullName: 'Miles per Minute',
      description: 'High-speed travel'
    },
    'meters_per_hour': { 
      factor: 0.000277778, 
      category: 'Time Variants', 
      symbol: 'm/h', 
      fullName: 'Meters per Hour',
      description: 'Very slow processes'
    },
    'feet_per_hour': { 
      factor: 0.0000847, 
      category: 'Time Variants', 
      symbol: 'ft/h', 
      fullName: 'Feet per Hour',
      description: 'Geological processes'
    },
    
    // Novelty/Historical
    'furlongs_per_fortnight': { 
      factor: 0.000166309, 
      category: 'Novelty', 
      symbol: 'fur/fn', 
      fullName: 'Furlongs per Fortnight',
      description: 'Humorous unit (very slow)'
    },
    'beaufort_scale': { 
      factor: 0.836, 
      category: 'Novelty', 
      symbol: 'Bf', 
      fullName: 'Beaufort Scale',
      description: 'Wind speed scale (approximate)'
    }
  };

  const categories = Array.from(new Set(Object.values(units).map(unit => unit.category)));

  const convert = (value: number, from: UnitName, to: UnitName): number => {
    if (!value && value !== 0 || isNaN(value)) return 0;
    const fromFactor = units[from].factor;
    const toFactor = units[to].factor;
    const result = (value * fromFactor) / toFactor;
    return parseFloat(result.toPrecision(precision));
  };

  const handleFromValueChange = (value: string): void => {
    setFromValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), fromUnit, toUnit);
      setToValue(converted.toString());
    } else {
      setToValue('');
    }
  };

  const handleToValueChange = (value: string): void => {
    setToValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), toUnit, fromUnit);
      setFromValue(converted.toString());
    } else {
      setFromValue('');
    }
  };

  const swapUnits = (): void => {
    const tempUnit = fromUnit;
    const tempValue = fromValue;
    
    setFromUnit(toUnit);
    setToUnit(tempUnit);
    setFromValue(toValue);
    setToValue(tempValue);
  };

  const addToHistory = (): void => {
    if ((fromValue || fromValue === '0') && (toValue || toValue === '0')) {
      const entry: HistoryEntry = {
        id: Date.now(),
        from: `${fromValue} ${units[fromUnit].symbol}`,
        to: `${toValue} ${units[toUnit].symbol}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setHistory(prev => [entry, ...prev.slice(0, 9)]);
    }
  };

  const copyResult = (): void => {
    if (toValue || toValue === '0') {
      navigator.clipboard.writeText(`${toValue} ${units[toUnit].symbol}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = (): void => {
    setFromValue('');
    setToValue('');
  };

  const getSpeedVisualization = (speed: number, unit: UnitName): string[] => {
    const metersPerSecond = speed * units[unit].factor;
    const visualizations: string[] = [];
    
    if (metersPerSecond < 0.001) visualizations.push('Slower than snail pace');
    else if (metersPerSecond < 0.1) visualizations.push('Ant walking speed');
    else if (metersPerSecond < 1) visualizations.push('Leisurely walking');
    else if (metersPerSecond < 3) visualizations.push('Brisk walking');
    else if (metersPerSecond < 8) visualizations.push('Running speed');
    else if (metersPerSecond < 15) visualizations.push('Cycling speed');
    else if (metersPerSecond < 30) visualizations.push('City driving');
    else if (metersPerSecond < 50) visualizations.push('Highway driving');
    else if (metersPerSecond < 100) visualizations.push('High-speed train');
    else if (metersPerSecond < 200) visualizations.push('Racing car');
    else if (metersPerSecond < 343) visualizations.push('Jet aircraft');
    else if (metersPerSecond < 1000) visualizations.push('Supersonic flight');
    else if (metersPerSecond < 11200) visualizations.push('Rocket speed');
    else if (metersPerSecond < 299792458) visualizations.push('Relativistic speed');
    else visualizations.push('Light speed');
    
    return visualizations;
  };

  const getSpeedComparisons = (speed: number, unit: UnitName): string[] => {
    const metersPerSecond = speed * units[unit].factor;
    const comparisons: string[] = [];
    
    // Common speed comparisons
    const speedRefs = [
      { name: 'Walking', speed: 1.4, tolerance: 0.5 },
      { name: 'Running', speed: 6, tolerance: 2 },
      { name: 'Cycling', speed: 12, tolerance: 4 },
      { name: 'Car in city', speed: 14, tolerance: 6 },
      { name: 'Highway speed', speed: 28, tolerance: 8 },
      { name: 'Cheetah', speed: 30, tolerance: 5 },
      { name: 'High-speed train', speed: 83, tolerance: 20 },
      { name: 'Commercial jet', speed: 250, tolerance: 50 },
      { name: 'Sound speed', speed: 343, tolerance: 10 },
      { name: 'Bullet', speed: 400, tolerance: 200 }
    ];
    
    for (const ref of speedRefs) {
      if (Math.abs(metersPerSecond - ref.speed) <= ref.tolerance) {
        comparisons.push(`Similar to ${ref.name}`);
      }
    }
    
    return comparisons.slice(0, 2);
  };

  const getCommonEquivalents = (speed: number, unit: UnitName): string[] => {
    const metersPerSecond = speed * units[unit].factor;
    const equivalents: string[] = [];
    
    if (unit !== 'kilometers_per_hour') {
      const kmh = metersPerSecond / units.kilometers_per_hour.factor;
      equivalents.push(`≈ ${kmh.toFixed(1)} km/h`);
    }
    
    if (unit !== 'miles_per_hour') {
      const mph = metersPerSecond / units.miles_per_hour.factor;
      equivalents.push(`≈ ${mph.toFixed(1)} mph`);
    }
    
    if (unit !== 'knots') {
      const knots = metersPerSecond / units.knots.factor;
      equivalents.push(`≈ ${knots.toFixed(1)} knots`);
    }
    
    return equivalents.slice(0, 2);
  };

  const getSpeedCategory = (speed: number, unit: UnitName): string => {
    const metersPerSecond = speed * units[unit].factor;
    
    if (metersPerSecond < 1) return 'Very Slow';
    if (metersPerSecond < 5) return 'Slow';
    if (metersPerSecond < 15) return 'Moderate';
    if (metersPerSecond < 50) return 'Fast';
    if (metersPerSecond < 200) return 'Very Fast';
    if (metersPerSecond < 343) return 'Subsonic';
    if (metersPerSecond < 1715) return 'Supersonic';
    if (metersPerSecond < 11200) return 'Hypersonic';
    return 'Extreme';
  };

  useEffect(() => {
    if ((fromValue || fromValue === '0') && !isNaN(Number(fromValue))) {
      const converted = convert(parseFloat(fromValue), fromUnit, toUnit);
      setToValue(converted.toString());
    }
  }, [fromUnit, toUnit, precision, fromValue]);

  const renderUnitOptions = (): React.ReactElement[] => {
    return categories.map(category => (
      <optgroup key={category} label={category}>
        {Object.entries(units)
          .filter(([_, unit]) => unit.category === category)
          .map(([unitName, unit]) => (
            <option key={unitName} value={unitName}>
              {unit.fullName} ({unit.symbol})
            </option>
          ))}
      </optgroup>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-yellow-50 to-orange-100 min-h-screen">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Zap className="w-8 h-8 text-yellow-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Advanced Speed Converter</h1>
          </div>
          <p className="text-gray-600">Convert between multiple speed units with precision and comparisons</p>
        </div>

        <div className="flex flex-wrap gap-4 mb-6 justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Precision:</label>
            <select 
              value={precision} 
              onChange={(e) => setPrecision(parseInt(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            >
              {[2, 3, 4, 5, 6, 8, 10].map(p => (
                <option key={p} value={p}>{p} digits</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            History
          </button>
          
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Clear All
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              From
            </h3>
            <div className="space-y-3">
              <input
                type="number"
                value={fromValue}
                onChange={(e) => handleFromValueChange(e.target.value)}
                placeholder="Enter speed"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[fromUnit].description}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              To
            </h3>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="number"
                  value={toValue}
                  onChange={(e) => handleToValueChange(e.target.value)}
                  placeholder="Result"
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={copyResult}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Copy result"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[toUnit].description}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mb-6">
          <button
            onClick={swapUnits}
            className="flex items-center gap-2 px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors shadow-md"
          >
            <ArrowLeftRight className="w-5 h-5" />
            Swap Units
          </button>
        </div>

        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="flex justify-center mb-6">
            <button
              onClick={addToHistory}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              Add to History
            </button>
          </div>
        )}

        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Speed Analysis</h4>
            <p className="text-lg mb-3">
              <span className="font-mono bg-white px-2 py-1 rounded">{fromValue} {units[fromUnit].symbol}</span>
              <span className="mx-3 text-gray-500">=</span>
              <span className="font-mono bg-white px-2 py-1 rounded font-semibold text-yellow-700">{toValue} {units[toUnit].symbol}</span>
            </p>
            
            {fromValue && !isNaN(Number(fromValue)) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    getSpeedCategory(parseFloat(fromValue), fromUnit) === 'Very Slow' ? 'bg-blue-100 text-blue-800' :
                    getSpeedCategory(parseFloat(fromValue), fromUnit) === 'Slow' ? 'bg-green-100 text-green-800' :
                    getSpeedCategory(parseFloat(fromValue), fromUnit) === 'Moderate' ? 'bg-yellow-100 text-yellow-800' :
                    getSpeedCategory(parseFloat(fromValue), fromUnit) === 'Fast' ? 'bg-orange-100 text-orange-800' :
                    getSpeedCategory(parseFloat(fromValue), fromUnit) === 'Very Fast' ? 'bg-red-100 text-red-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {getSpeedCategory(parseFloat(fromValue), fromUnit)}
                  </span>
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Speed Comparison:</strong>
                  {getSpeedVisualization(parseFloat(fromValue), fromUnit).map(viz => (
                    <span key={viz} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {viz}
                    </span>
                  ))}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Common Equivalents:</strong>
                  {getCommonEquivalents(parseFloat(fromValue), fromUnit).map(equiv => (
                    <span key={equiv} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {equiv}
                    </span>
                  ))}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Similar To:</strong>
                  {getSpeedComparisons(parseFloat(fromValue), fromUnit).map(comp => (
                    <span key={comp} className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {comp}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Speed Reference Guide</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="space-y-1">
              <h5 className="font-medium text-blue-800">Human Speeds</h5>
              <div>Walking: 5 km/h</div>
              <div>Running: 20 km/h</div>
              <div>Sprinting: 40 km/h</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-green-800">Transportation</h5>
              <div>Bicycle: 25 km/h</div>
              <div>Car (city): 50 km/h</div>
              <div>Highway: 100 km/h</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-orange-800">Animals</h5>
              <div>Cheetah: 110 km/h</div>
              <div>Horse: 70 km/h</div>
              <div>Dolphin: 60 km/h</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-red-800">Extreme Speeds</h5>
              <div>Sound: 1,235 km/h</div>
              <div>Jet: 900 km/h</div>
              <div>Light: 1.08×10⁹ km/h</div>
            </div>
          </div>
        </div>

        {showHistory && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <History className="w-5 h-5" />
              Conversion History
            </h4>
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No conversions in history</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{entry.from}</span>
                      <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm font-semibold">{entry.to}</span>
                    </div>
                    <span className="text-xs text-gray-500">{entry.timestamp}</span>
                  </div>
                ))}
              </div>
            )}
            {history.length > 0 && (
              <button
                onClick={() => setHistory([])}
                className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm"
              >
                Clear History
              </button>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map(category => (
            <div key={category} className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-800 mb-2">{category}</h5>
              <div className="space-y-1">
                {Object.entries(units)
                  .filter(([_, unit]) => unit.category === category)
                  .slice(0, 4)
                  .map(([unitName, unit]) => (
                    <div key={unitName} className="text-sm text-gray-600">
                      {unit.symbol} - {unit.fullName}
                    </div>
                  ))}
                {Object.entries(units).filter(([_, unit]) => unit.category === category).length > 4 && (
                  <div className="text-xs text-gray-500">+ more...</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpeedConverter;